import { Fragment } from "react";
import { KeywordTitle } from "./Hl";
import { Link } from "react-router";
import { t } from "../i18n";
import { inlineParts, landingByPath, type LandingPage as Page } from "../landing";
import { LANDING_SOURCES, guideAnchor, type GuideId } from "../guides";
import { SourceIcon } from "./brand-icons";
import { PriceNote } from "./PriceNote";

const sourceTitle = (id: string) => LANDING_SOURCES.find((s) => s.id === id)?.label ?? id;

/**
 * Text with `[text](href)` links as plain HTML: a site route → react-router Link, a static file
 * (`/privacy.html`) → <a>, an external `https://` address → <a> in a new tab.
 */
function Inline({ text }: { text: string }) {
  return (
    <>
      {inlineParts(text).map((p, i) => {
        if (!("href" in p)) return <Fragment key={i}>{p.text}</Fragment>;
        if (/^https?:\/\//.test(p.href)) {
          return (
            <a key={i} href={p.href} target="_blank" rel="noopener">
              {p.text}
            </a>
          );
        }
        const path = p.href.split("#")[0]!;
        if (path.includes(".")) {
          return (
            <a key={i} href={p.href}>
              {p.text}
            </a>
          );
        }
        return (
          <Link key={i} to={p.href}>
            {p.text}
          </Link>
        );
      })}
    </>
  );
}

/** A source landing page (ADR-0036): all text is plain HTML, nothing here needs JavaScript. */
export function LandingPage({ page }: { page: Page }) {
  const related = page.related.map((p) => landingByPath(p)).filter((p): p is Page => !!p);
  const parent = page.parent ? landingByPath(page.parent) : undefined;
  return (
    <article className="landing">
      <header className="landing-hero">
        {parent && (
          <p>
            <Link className="text-link" to={parent.path}>
              {t("landing.back", { name: parent.breadcrumb })}
            </Link>
          </p>
        )}
        {page.kicker && <p className="kicker">{page.kicker}</p>}
        <h1><KeywordTitle text={page.h1} /></h1>
        <p className="hero-lead">{page.lead}</p>
        {page.sources.length > 0 && (
          <ul className="landing-marks" aria-label={t("landing.sources")}>
            {page.sources.map((id) => (
              <li key={id} className="landing-mark">
                <SourceIcon id={id} size={16} />
                <span>{sourceTitle(id)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="landing-cta">
          <Link className="btn btn-primary" to={page.installHref ?? "/install"}>
            {t("landing.installCta")}
          </Link>
          {page.connectAnchor && (
            <Link className="btn btn-secondary" to={`/connections#${page.connectAnchor}`}>
              {t("landing.connectCta", { source: sourceTitle(page.connectAnchor) })}
            </Link>
          )}
        </div>
        <PriceNote />
        {page.checked && <p className="landing-checked">{t("landing.checked", { date: page.checked })}</p>}
      </header>

      {page.sections.map((s) => (
        <section key={s.h2} className="landing-section">
          <h2>{s.h2}</h2>
          {s.paragraphs?.map((p) => (
            <p key={p}>
              <Inline text={p} />
            </p>
          ))}
          {s.bullets && (
            <ul>
              {s.bullets.map((b) => (
                <li key={b}>
                  <Inline text={b} />
                </li>
              ))}
            </ul>
          )}
          {s.steps && (
            <ol>
              {s.steps.map((b) => (
                <li key={b}>
                  <Inline text={b} />
                </li>
              ))}
            </ol>
          )}
        </section>
      ))}

      {page.table && (
        <section className="landing-section">
          <div className="landing-table table-scroll">
            <table className="table">
              <caption>{page.table.caption}</caption>
              <thead>
                <tr>{page.table.head.map((h) => <th key={h} scope="col">{h}</th>)}</tr>
              </thead>
              <tbody>
                {page.table.rows.map((r) => (
                  <tr key={r.join("|")}>{r.map((c, i) => (i === 0 ? <th key={i} scope="row">{c}</th> : <td key={i}>{c}</td>))}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {page.faq && page.faq.length > 0 && (
        <section className="landing-section landing-faq">
          <h2>{t("landing.faq")}</h2>
          {page.faq.map((f) => (
            <div key={f.q}>
              <h3>{f.q}</h3>
              <p>
                <Inline text={f.a} />
              </p>
            </div>
          ))}
        </section>
      )}

      {related.length > 0 && (
        <nav className="landing-section" aria-label={t("landing.related")}>
          <h2>{t("landing.related")}</h2>
          <ul className="landing-related">
            {related.map((p) => (
              <li key={p.path}>
                <Link className="text-link" to={p.path}>{p.h1} →</Link>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <p className="landing-privacy">
        <Link className="text-link" to={page.connectAnchor ? `/guide#${guideAnchor(page.connectAnchor as GuideId)}` : "/guide"}>
          {page.connectAnchor ? t("landing.guide", { source: sourceTitle(page.connectAnchor) }) : t("landing.guideAll")}
        </Link>
      </p>
      <p className="landing-privacy">
        {t("landing.privacy")} <a href="/privacy.html">{t("landing.privacyLink")}</a>
      </p>
      {page.footnote && <p className="landing-footnote">{page.footnote}</p>}
    </article>
  );
}
